import React, { useEffect, useMemo, useState, useRef } from 'react'
import { useAuth } from '../contexts/auth-context'
import { Link, useNavigate } from 'react-router-dom';
import { FIGHTER_EDIT_PATH, FIGHTER_REGISTER_PATH, LANDING_PATH, LOGIN_PATH, FIGHTER_PATH } from '../App';
import { signOut } from 'firebase/auth';
import { auth, fetchAllTokensForFighter, fetchEvent, fighterRequestsCollection } from '../utils/firebase';
import { and, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { extractTimestamp, formatFirestoreTimestamp } from '../utils/common';
import { fighterRequestType, fighterRequestStatus, tokenStatus, weightClasses } from '../constants';
import { CircleCheckBig, Clipboard, CoinsIcon, Minus, Pen, Plus, X, AlertCircle } from 'lucide-react';
import Preview from '../components/preview';
import FighterRecordsTable from '../components/fighter-records-table';
import { twMerge } from 'tailwind-merge';
import { ProfileTable } from '../components/profile-table';
import toast from 'react-hot-toast';
import EventRegisterCard from '../components/event-register-card';
import { useTranslation } from 'react-i18next';
import QRCodeStyling from 'qr-code-styling';
import { IdCard } from 'lucide-react';

export default function Profile() {
    const { t } = useTranslation();
    const { currentUser, refreshUser } = useAuth();
    const navigate = useNavigate()
    const shareModal = useRef(null);
    const qrCanvas = useRef(null);
    const [qrCode, setQrCode] = useState(null);

    const [newRegister, setNewRegister] = useState(null)
    const [eventRegisters, setEventRegisters] = useState(null)
    const [tokens, setTokens] = useState(null)
    const [eventNameById, setEventNameById] = useState({})
    const [loading, setLoading] = useState(true)
    const [currentTab, setCurrentTab] = useState("personalInfo");

    const tabs = useMemo(() => ["personalInfo", "registeredEvent", "credits"], [])

    const handleLogout = async () => {
        await signOut(auth)
        navigate(LOGIN_PATH);
    }

    useEffect(() => {
        const defaultTab = (new URLSearchParams(window.location.search)).get("tab")
        if (tabs.includes(defaultTab)) {
            setCurrentTab(defaultTab)
        }
    }, [tabs])

    useEffect(() => {
        // Bail when the user has logged out — otherwise Firestore queries fire
        // with an undefined uid and hit a rules denial / SDK validation error.
        if (!currentUser?.uid) {
            setLoading(false);
            return;
        }
        (async () => {
            try {
                await refreshUser();
                // If not fighter
                if (currentUser?.role !== "fighter") {
                    // Then fetch for fighterRequest to check the status
                    const newRegisterQuery = query(fighterRequestsCollection, and(where("uid", "==", currentUser?.uid), where("type", "==", fighterRequestType.newRegister)), limit(1), orderBy("updateAt", "desc"))

                    const newRegisterSnapshot = await getDocs(newRegisterQuery);
                    if (!newRegisterSnapshot.empty) {
                        setNewRegister({
                            id: newRegisterSnapshot.docs[0].id,
                            ...newRegisterSnapshot.docs[0].data()
                        })
                    }
                } else {
                    if (!currentUser?.fighter || currentUser?.role !== "fighter") {
                        await refreshUser();
                    }

                    if (!!currentUser?.fighter) {

                        const eventRegistersQuery = query(fighterRequestsCollection, and(where("uid", "==", currentUser?.uid), where("type", "==", fighterRequestType.registerEvent)), orderBy("updateAt", "desc"))

                        const eventRegistersSnapshot = await getDocs(eventRegistersQuery);
                        if (!eventRegistersSnapshot.empty) {
                            setEventRegisters([...eventRegistersSnapshot.docs.map((er) => er.data())])
                        } else {
                            setEventRegisters([])
                        }

                        const fetchedTokens = await fetchAllTokensForFighter(currentUser?.uid)
                        setTokens(fetchedTokens)
                    } else {
                        toast.error("Something went wrong.")
                        navigate("/404")
                    }
                }
            } catch (err) {
                // Most common cause: logout race — auth invalidated mid-query.
                // Swallow the denial so React doesn't surface an Uncaught overlay.
                // eslint-disable-next-line no-console
                console.warn("Profile load aborted (likely logout race):", err?.message ?? err);
            } finally {
                setLoading(false)
            }
        })()
    }, [currentUser?.fighter, currentUser?.role, currentUser?.uid, navigate, refreshUser])

    // Backfill event names for legacy tokens that don't have sourceEventName /
    // usedForEventName denormalized on them. Newer tokens already carry the
    // name and skip this fetch entirely.
    useEffect(() => {
        if (!tokens || tokens.length === 0) return
        const missingIds = new Set()
        tokens.forEach((token) => {
            if (token.sourceEventId && !token.sourceEventName) {
                missingIds.add(token.sourceEventId)
            }
            if (token.usedForEventId && !token.usedForEventName) {
                missingIds.add(token.usedForEventId)
            }
        })
        const toFetch = [...missingIds].filter((id) => !(id in eventNameById))
        if (toFetch.length === 0) return
        let cancelled = false
            ; (async () => {
                const entries = await Promise.allSettled(
                    toFetch.map((id) =>
                        fetchEvent({ id }).then((r) => ({
                            id,
                            name: r?.data?.name ?? null,
                        }))
                    )
                )
                if (cancelled) return
                const next = {}
                entries.forEach((res) => {
                    if (res.status === "fulfilled") {
                        next[res.value.id] = res.value.name
                    }
                })
                if (Object.keys(next).length > 0) {
                    setEventNameById((prev) => ({ ...prev, ...next }))
                }
            })()
        return () => {
            cancelled = true
        }
    }, [tokens, eventNameById])

    const handleShare = () => {
        if (shareModal?.current) {
            shareModal.current.showModal();
        }
    }

    const handleDownloadQr = () => {
        if (!qrCode) return;
        qrCode.download({
            extension: "png"
        });
    };

    const handleCopyUrl = () => {
        if (navigator?.clipboard) {
            navigator.clipboard.writeText(`${window.location.origin}/fighter/${currentUser?.uid}`);
            toast("📋 Copied Profile URL to Clipboard");
        }
    }

    useEffect(() => {
        if (!!currentUser?.fighter) {
            setQrCode(new QRCodeStyling({
                width: "240",
                height: "240",
                type: 'png',
                data: `${window.location.origin}/fighter/${currentUser?.uid}`,
                image: '/assets/img/logo-mfl.png',
                margin: 4,
                qrOptions: {
                    typeNumber: 0,
                    mode: 'Byte',
                    errorCorrectionLevel: 'Q'
                },
                imageOptions: {
                    hideBackgroundDots: true,
                    imageSize: 0.4,
                    margin: 4,
                    crossOrigin: 'anonymous',
                },
                dotsOptions: {
                    color: '#222222',
                    type: 'classy-rounded'
                },
                backgroundOptions: {
                    color: 'white',
                },
                cornersSquareOptions: {
                    color: '#222222',
                },
                cornersDotOptions: {
                    color: '#222222',
                }
            }))
        }
    }, [currentUser?.fighter])

    useEffect(() => {
        if (qrCanvas.current) {
            qrCanvas.current.innerHTML = null;
            if (qrCode) {
                qrCode.append(qrCanvas.current);
            }
        }
    }, [qrCode])

    if (loading) {
        return (
            <div className='w-full min-h-[calc(100vh-6.5rem)] flex-center'>
                <div className="loading loading-md loading-bars" />
            </div>
        )
    }

    if (currentUser.role?.toUpperCase() !== "FIGHTER" && !newRegister) {
        return (
            <div className="w-full min-h-[calc(100vh-6.5rem)] flex-center">
                <div className="flex-center gap-2">
                    <Link to={FIGHTER_REGISTER_PATH} className='btn btn-primary w-full'>
                        {t('profile.becomeFighter')}
                    </Link>
                    <button className='btn btn-ghost w-full group' onClick={handleLogout}>
                        <span className='inline group-hover:hidden underline'>
                            {t('profile.notThisAccount')}
                        </span>
                        <span className='hidden group-hover:inline font-bold'>
                            {t('profile.logout')}
                        </span>
                    </button>
                </div>
            </div>
        )
    }

    if (currentUser.role?.toUpperCase() !== "FIGHTER" && !!newRegister) {
        const isRejected = newRegister?.status?.toUpperCase() === fighterRequestStatus.rejected;
        
        return (
            <>
                <div className='w-full min-h-[calc(100vh-6.5rem)] flex-center p-2 md:p-4 gap-2'>
                    <div className="flex-center w-full min-h-[50vh]">
                        {/* Conditional Icon and Message */}
                        {isRejected ? (
                            <p className='text-xl text-center mb-4 max-w-[520px]'>
                                <X className='mr-2 inline text-error' size={24} />
                                <br/>
                                {t('profile.rejectedForm')}
                            </p>
                        ) : (
                            <p className='text-xl text-center mb-4 max-w-[520px]'>
                                <CircleCheckBig className='mr-2 inline text-green-400' />
                                <br/>
                                {t('profile.submittedForm')}
                            </p>
                        )}
                        
                        {/* Status Badge with Conditional Color */}
                        <p className='text-lg'>
                            {t('profile.currentStatus')}
                            <span className={twMerge(
                                "font-bold badge ml-2",
                                isRejected && "badge-error",
                                newRegister?.status?.toUpperCase() === fighterRequestStatus.approved && "badge-success",
                                newRegister?.status?.toUpperCase() === fighterRequestStatus.pending && "badge-warning"
                            )}>
                                {newRegister?.status?.toUpperCase() ?? "?"}
                            </span>
                        </p>
                    </div>

                    {/* Rejection Alert - Only show for rejected */}
                    {isRejected && (
                        <div className="alert alert-error mt-4 max-w-[67rem]">
                            <AlertCircle />
                            <div>
                                <h3 className="font-bold">{t('profile.rejectionTitle')}</h3>
                                <div className="text-sm">{t('profile.rejectionMessage')}</div>
                            </div>
                        </div>
                    )}

                    {/* Comments Section */}
                    <div className="w-full max-w-[67rem] flex flex-row justify-between items-center">
                        <p className='w-full opacity-70'>{t('profile.comments')}</p>
                    </div>
                    <div className="border w-full max-w-[67rem] p-4">
                        <div>
                            {
                                !!newRegister.comments && newRegister.comments.length > 0
                                    ? <div className='flex-center items-start w-full gap-2'>
                                        {
                                            !newRegister?.comments || newRegister.comments.length < 1
                                                ? <div className="w-full h-full flex-center border border-base-300 p-4">
                                                    <p>{t('profile.noComments')}</p>
                                                </div>
                                                : (newRegister.comments).map((comment, index) => {
                                                    const createdAt = !comment.createdAt ? null : extractTimestamp(comment.createdAt)
                                                    return (
                                                        <div className='w-full ' key={index}>
                                                            <div className="chat chat-start">
                                                                <div className="chat-bubble">
                                                                    <p className=''>
                                                                        {comment.text ?? ""}
                                                                    </p>
                                                                </div>
                                                                <div className="chat-footer">
                                                                    {
                                                                        !!createdAt &&
                                                                        <p className='text-sm opacity-80'>{createdAt.day} {createdAt.month} {createdAt.year}, {createdAt.hour}:{createdAt.minute}</p>
                                                                    }
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )
                                                })
                                        }
                                    </div>
                                    : t('profile.noComments')
                            }
                        </div>
                    </div>

                    {/* Conditional Rendering: Hide preview for rejected, show for pending/approved */}
                    {!isRejected ? (
                        <>
                            <br />
                            <div className="w-full max-w-[67rem] flex flex-row justify-between items-center">
                                <p className='opacity-70'>{t('profile.preview')}</p>
                                <Link to={`${FIGHTER_REGISTER_PATH}/${newRegister.id}`} className='btn btn-primary btn-sm'>
                                    <Pen className='inline' size={16} />
                                    {t('profile.edit')}
                                </Link>
                            </div>
                            <div className="border w-full max-w-[67rem] p-4">
                                <div className="flex flex-col gap-6">
                                    <div className="flex flex-col md:flex-row gap-2">
                                        <p className="font-bold md:w-1/3">{t('profile.fields.profilePicture')}</p>
                                        <div className='flex-center flex-1'>
                                            <div className="flex flex-row w-full gap-2 justify-stretch">
                                                <div className="flex-center w-full">
                                                    {
                                                        !newRegister?.data?.fighterPicture
                                                            ? null
                                                            : <Preview src={newRegister?.data?.fighterPicture} className={"max-w-52 aspect-square object-cover object-center"} />
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex flex-col md:flex-row gap-2">
                                        <p className="font-bold md:w-1/3">{t('profile.fields.email')}</p>
                                        <p className="flex-1">{newRegister.data?.email}</p>
                                    </div>

                                    <div className="flex flex-col md:flex-row gap-2">
                                        <p className="font-bold md:w-1/3">{t('profile.fields.fullName')}</p>
                                        <p className="flex-1">{newRegister.data?.name}</p>
                                    </div>

                                    <div className="flex flex-col md:flex-row gap-2">
                                        <p className="font-bold md:w-1/3">{t('profile.fields.gender')}</p>
                                        <p className="flex-1">{!newRegister.data?.gender ? "-" : newRegister.data?.gender.toUpperCase() === "M" ? t('profile.gender.male') : newRegister.data?.gender.toUpperCase() === "F" ? t('profile.gender.female') : t('profile.gender.unknown')}</p>
                                    </div>

                                    <div className="flex flex-col md:flex-row gap-2">
                                        <p className="font-bold md:w-1/3">{t('profile.fields.dateOfBirth')}</p>
                                        <p className="flex-1">{!newRegister?.data?.birthdate ? "-" : formatFirestoreTimestamp(newRegister?.data?.birthdate)}</p>
                                    </div>

                                    <div className="flex flex-col md:flex-row gap-2">
                                        <p className="font-bold md:w-1/3">{t('profile.fields.age')}</p>
                                        <p className="flex-1">{newRegister?.data?.age ?? 0}</p>
                                    </div>

                                    <div className="flex flex-col md:flex-row gap-2">
                                        <p className="font-bold md:w-1/3">{t('profile.fields.height')}</p>
                                        <p className="flex-1">{newRegister?.data?.height}</p>
                                    </div>

                                    <div className="flex flex-col md:flex-row gap-2">
                                        <p className="font-bold md:w-1/3">{t('profile.fields.weightClass')}</p>
                                        <p className="flex-1">{newRegister?.data?.weightClass}: {weightClasses[newRegister?.data?.weightClass ?? "-"] ?? "-"}</p>
                                    </div>

                                    <div className="flex flex-col md:flex-row gap-2">
                                        <p className="font-bold md:w-1/3">{t('profile.fields.nationality')}</p>
                                        <p className="flex-1">{newRegister?.data?.nationality ?? "-"}</p>
                                    </div>

                                    <div className="flex flex-col md:flex-row gap-2">
                                        <p className="font-bold md:w-1/3">{t('profile.fields.idPassport')}</p>
                                        <p className="flex-1">{newRegister?.data?.icOrPassport ?? "-"}</p>
                                    </div>

                                    <div className="flex flex-col md:flex-row gap-2">
                                        <p className="font-bold md:w-1/3">{t('profile.fields.phoneNumber')}</p>
                                        <p className="flex-1">{newRegister?.data?.phoneNumber ?? "-"}</p>
                                    </div>

                                    <div className="flex flex-col md:flex-row gap-2">
                                        <p className="font-bold md:w-1/3">{t('profile.fields.emergencyContact')}</p>
                                        <p className="flex-1">{newRegister?.data?.emergencyContactNumber ?? "-"}</p>
                                    </div>

                                    <div className="flex flex-col md:flex-row gap-2">
                                        <p className="font-bold md:w-1/3">{t('profile.fields.instagram')}</p>
                                        <p className="flex-1">{newRegister?.data?.igUsername ?? "-"}</p>
                                    </div>

                                    <div className="flex flex-col md:flex-row gap-2">
                                        <p className="font-bold md:w-1/3">{t('profile.fields.gymTeam')}</p>
                                        <p className="flex-1">{newRegister?.data?.gymNameOrTeamName ?? "-"}</p>
                                    </div>

                                    <div className="flex flex-col md:flex-row gap-2">
                                        <p className="font-bold md:w-1/3">{t('profile.fields.walkoutMusic')}</p>
                                        <p className="flex-1">{newRegister?.data?.walkOutMusic ?? "-"}</p>
                                    </div>

                                    <div className="flex flex-col md:flex-row gap-2 border-t-2 pt-2">
                                        <p className="font-bold md:w-1/3">{t('profile.fields.records')}</p>
                                        <div className="flex-1">
                                            <FighterRecordsTable records={newRegister?.data?.records} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {
                                !newRegister?.updateAt ? null :
                                    <p className="w-10/12 opacity-80 text-end">
                                        {t('profile.lastUpdated')} {
                                            formatFirestoreTimestamp(newRegister.updateAt)
                                        }
                                    </p>
                            }
                        </>
                    ) : (
                        <>
                            {/* For rejected: Show call-to-action buttons */}
                            <div className="w-full max-w-[67rem] flex-center mt-6 gap-4 flex-col md:flex-row">
                                <Link 
                                    to={`${FIGHTER_REGISTER_PATH}/${newRegister.id}`} 
                                    className='btn btn-error btn-lg flex-1'
                                >
                                    <Pen className='inline mr-2' size={20} />
                                    {t('profile.editAndResubmit')}
                                </Link>
                                <Link 
                                    to={FIGHTER_REGISTER_PATH} 
                                    className='btn btn-outline btn-lg flex-1'
                                >
                                    {t('profile.submitNewApplication')}
                                </Link>
                            </div>
                            <div className="w-full max-w-[67rem] flex-center mt-4">
                                <button 
                                    className='btn btn-ghost btn-sm' 
                                    onClick={handleLogout}
                                >
                                    {t('profile.logout')}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </>
        )
    }

    const personalInfo = (
        <div className='flex-center gap-2'>
            <div className="flex flex-row w-full gap-2">
                <div className="flex-1" />
                <Link to={FIGHTER_PATH.replace(":id", currentUser?.uid)} className="btn btn-primary btn-square btn-sm">
                    <IdCard size={16} />
                </Link>
                <Link to={`${FIGHTER_EDIT_PATH}/${currentUser.uid}`} className='btn btn-primary btn-sm'>
                    <Pen size={16} />
                    {t('profile.edit')}
                </Link>
            </div>
            <div className="p-4 w-full h-full border">
                <ProfileTable />
            </div>
        </div>
    )

    const activeTokenCount = (tokens ?? []).filter(token => token.status === tokenStatus.active).length

    // Build a transaction ledger from the tokens. Each issuance is a +1 row;
    // each redemption (only on USED tokens) is a -1 row. Sorted newest first.
    const resolveEventName = (id, denormalizedName) => {
        if (denormalizedName) return denormalizedName
        if (id && eventNameById[id]) return eventNameById[id]
        if (id) return id
        return t('profile.credits.unknownEvent')
    }
    const creditTransactions = (tokens ?? []).flatMap((token) => {
        const rows = [
            {
                id: `${token.id}_issued`,
                kind: "issued",
                at: token.issuedAt,
                eventName: resolveEventName(token.sourceEventId, token.sourceEventName),
            },
        ]
        if (token.status === tokenStatus.used && token.usedAt) {
            rows.push({
                id: `${token.id}_used`,
                kind: "used",
                at: token.usedAt,
                eventName: resolveEventName(token.usedForEventId, token.usedForEventName),
            })
        }
        return rows
    }).sort((a, b) => {
        const aMs = a.at?.toMillis?.() ?? 0
        const bMs = b.at?.toMillis?.() ?? 0
        return bMs - aMs
    })

    const credits = tokens === null
        ? <div className='flex-center w-full p-0 md:p-4'><div className='loading loading-bars loading-md' /></div>
        : (
            <div className='flex flex-col gap-4'>
                <div className='border p-4 flex flex-row items-center gap-4'>
                    <CoinsIcon size={36} className='text-yellow-300' />
                    <div className='flex-1'>
                        <p className='font-bold text-2xl'>{t('profile.credits.activeCount', { count: activeTokenCount })}</p>
                        <p className='text-sm opacity-80'>{t('profile.credits.description')}</p>
                    </div>
                </div>
                <p className='font-bold'>{t('profile.credits.historyTitle')}</p>
                {creditTransactions.length === 0 ? (
                    <div className='opacity-80 p-4 border'>{t('profile.credits.empty')}</div>
                ) : (
                    <div className='flex flex-col gap-2'>
                        {creditTransactions.map((tx) => {
                            const isIssued = tx.kind === "issued"
                            return (
                                <div
                                    key={tx.id}
                                    className='border p-3 flex flex-row items-center gap-3'
                                >
                                    {isIssued ? (
                                        <Plus size={32} className='text-success shrink-0 hidden md:block' strokeWidth={3} />
                                    ) : (
                                        <Minus size={32} className='text-error shrink-0 hidden md:block' strokeWidth={3} />
                                    )}
                                    <div className='flex flex-col flex-1'>
                                        <p className='font-semibold'>
                                            <span className={twMerge(
                                                'font-bold mr-2',
                                                isIssued ? 'text-success' : 'text-error'
                                            )}>
                                                {isIssued ? '+1' : '−1'}
                                            </span>
                                            {isIssued
                                                ? t('profile.credits.issuedHeadline', { event: tx.eventName })
                                                : t('profile.credits.usedHeadline', { event: tx.eventName })
                                            }
                                        </p>
                                        <p className='text-sm opacity-70'>
                                            {tx.at ? formatFirestoreTimestamp(tx.at) : '-'}
                                        </p>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>
        )

    const registeredEvent = eventRegisters === null
        ? <div className='flex-center w-full p-0 md:p-4'><div className='loading loading-bars loading-md' /></div>
        : (
            <>
                <div className='flex-center gap-4'>
                    {
                        eventRegisters.length < 1
                            ? <div className='opacity-80 p-4'>{t('profile.empty')}</div>
                            : eventRegisters.map((eventRegister, index) => {
                                return (
                                    <div className='w-full flex flex-col gap-2' key={index}>
                                        {
                                            index !== 0 && <div className="divider m-0" />
                                        }
                                        <EventRegisterCard eventRegister={eventRegister} />
                                    </div>
                                )
                            })
                    }
                </div>
            </>
        )

    return (
        <>
            <div className="fixed top-0 left-0 w-[calc(100vw-1rem)] p-4 flex flex-row justify-between items-center backdrop-blur-lg z-[999]">
                <Link to={LANDING_PATH}>
                    <img src="/assets/img/logo-mfl.png" className='w-12' alt="" />
                </Link>
                <button className='btn btn-error btn-outline btn-sm' onClick={handleLogout}>{t('profile.logout')}</button>
            </div>
            <div className='page-size flex-ceter p-4 mt-12'>
                <div role="tablist" className="tabs tabs-boxed">
                    {
                        tabs.map((tab, index) => {
                            return (
                                <button
                                    key={index}
                                    role="tab"
                                    className={twMerge("tab capitalize", tab === currentTab && "tab-active font-bold")}
                                    onClick={() => setCurrentTab(tab)}
                                >
                                    {t(`profile.tabs.${tab}`)}
                                </button>
                            )
                        })
                    }
                </div>
                <div className="w-full m-0 divider" />
                {currentTab === "personalInfo" && personalInfo}
                {currentTab === "registeredEvent" && registeredEvent}
                {currentTab === "credits" && credits}
            </div>

            {/* QR Code Share Modal */}
            <dialog ref={shareModal} className="modal">
                <div className="modal-box border">
                    <div className='flex flex-row justify-between items-center'>
                        <h3 className="font-bold text-lg">Share to others</h3>
                        <form method="dialog">
                            <button className='btn btn-ghost btn-square btn-sm'>
                                <X />
                            </button>
                        </form>
                    </div>
                    <br />
                    <div className='flex-center w-full gap-2'>
                        <div className='object-center object-contain aspect-square' ref={qrCanvas} />
                        <br />
                        <form className='w-full' method="dialog">
                            <button className="btn btn-outline w-full" onClick={handleDownloadQr}>
                                Download
                            </button>
                        </form>
                        <div className='flex flex-row justify-between items-center w-full border'>
                            <p className='truncate px-4'>
                                {`${window.location.origin}/fighter/${currentUser?.uid}`}
                            </p>
                            <form method="dialog">
                                <button className="btn btn-primary btn-small btn-square" onClick={handleCopyUrl}>
                                    <Clipboard />
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button>close</button>
                </form>
            </dialog>
        </>
    )
}
